'use client';

/**
 * Recipe filter UI — URL-param driven.
 *
 * Filters are stored in the URL so they are shareable, SSR-friendly, and
 * back-button safe. This component reads current state from useSearchParams()
 * and writes via router.push(..., { scroll: false }).
 *
 * Desktop: horizontal strip with an expandable panel
 * Mobile (<768px): bottom-sheet drawer with sticky Apply bar
 *
 * Cuisines/categories are loaded server-side and passed as props to avoid
 * a client-side waterfall.
 */

import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useCallback, useEffect, useId, useMemo, useRef, useState, useTransition } from 'react';
import { useTranslations } from 'next-intl';
import {
  parseRecipeFilters,
  serializeRecipeFilters,
  countActiveFilters,
  hasActiveFilters,
  EMPTY_FILTERS,
  COOK_TIME_PRESETS,
  VARIANT_KEYS,
  NO_DEFAULT_PARAM,
  type RecipeFilters,
  type DietaryKey,
  type VariantKey,
} from '@/lib/utils/parse-recipe-filters';
import { Button } from '@/components/ui/button';
import { Icon, type IconName } from '@/components/ui/icon';
import { OptionChip } from '@/components/ui/selection-controls';
import styles from './recipe-filters.module.css';

/** Filter-legend glyphs. Hashimoto is a tier filter, not a diet indicator, so
 *  it uses the sparkles mark rather than a DIET_ICON entry. */
const DIET_FILTER_ICON: Record<DietaryKey, IconName> = {
  vegan: 'sprout',
  vegetarian: 'leaf',
  glutenFree: 'wheat-off',
  dairyFree: 'milk-off',
  hashimoto: 'sparkles',
};

interface TaxonomyItem {
  id: string;
  name: string;
}

export interface RecipeFiltersProps {
  cuisines: TaxonomyItem[];
  categories: TaxonomyItem[];
  /**
   * Extra URL params to preserve when serializing filters (e.g. `q` on /recipes).
   * Keys listed here are read from searchParams and re-written to the URL.
   */
  preserveParams?: string[];
  /**
   * Whether to surface the Hashimoto-safe filter chip. Gated on the viewer
   * having opted into the Hashimoto dietary preference — irrelevant chips
   * just clutter the panel for everyone else. Defaults to false so the chip
   * is hidden for anonymous browsers and non-Hashimoto users.
   */
  showHashimoto?: boolean;
  /**
   * Whether to surface the per-user library filters (Cooked / In cookbook).
   * Signed-in only — the public page hides them for anonymous visitors.
   */
  showLibrary?: boolean;
}

const DIETARY_KEYS_ALL: DietaryKey[] = ['vegan', 'vegetarian', 'glutenFree', 'dairyFree', 'hashimoto'];

// Maps a max-cook-time minute value to its translated label key.
// Falls back to a generated string when the preset isn't pre-translated.
const COOK_TIME_KEY_BY_MIN: Record<number, string> = {
  15: 'lt15',
  20: 'lt20',
  30: 'lt30',
  45: 'lt45',
  60: 'lt60',
  90: 'lt90',
};

export function RecipeFilters({
  cuisines,
  categories,
  preserveParams = [],
  showHashimoto = false,
  showLibrary = false,
}: RecipeFiltersProps) {
  const t = useTranslations();
  const dietaryLabels: Record<DietaryKey, string> = {
    vegan: t('dietary.vegan'),
    vegetarian: t('dietary.vegetarian'),
    glutenFree: t('dietary.glutenFree'),
    dairyFree: t('dietary.dairyFree'),
    hashimoto: t('dietary.hashimotoSafe'),
  };
  // Drop the Hashimoto chip for viewers who haven't opted into the preference.
  // The chip stays in dietaryLabels so type-checking + URL parsing still
  // recognise the value (e.g. shared links continue to work).
  const visibleDietaryKeys = DIETARY_KEYS_ALL.filter(
    (k) => showHashimoto || k !== 'hashimoto',
  );
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const toggleBtnRef = useRef<HTMLButtonElement>(null);

  // Derive current filters + preserved params from URL
  const { filters, preservedExtras } = useMemo(() => {
    const raw: Record<string, string | undefined> = {};
    searchParams.forEach((v, k) => {
      raw[k] = v;
    });
    const extras: Record<string, string | undefined> = {};
    for (const key of preserveParams) {
      const v = searchParams.get(key);
      if (v !== null) extras[key] = v;
    }
    return {
      filters: parseRecipeFilters(raw),
      preservedExtras: extras,
    };
  }, [searchParams, preserveParams]);

  const activeCount = countActiveFilters(filters);

  // Write filters to URL (debounced via transition, no scroll jump)
  const applyFilters = useCallback(
    (next: RecipeFilters) => {
      // An empty dietary set is serialised as NO `dietary` param at all, which
      // the server cannot tell apart from a first landing — so without a marker
      // the profile preference re-seeds it and the filter appears stuck (#623).
      //
      // The marker belongs here, on the CONDITION, rather than on any one
      // action: deselecting the last chip, "Clear all", and the drawer's clear
      // all produce the same empty set and all mean the same thing. Stamping it
      // in `clearAll` alone is what made chip deselection silently revert.
      //
      // Self-maintaining: while `dietary` is non-empty the marker does nothing
      // (the seed already skips when the param is present), and it is re-added
      // the moment the set becomes empty again.
      const extras =
        next.dietary.length === 0
          ? { ...preservedExtras, [NO_DEFAULT_PARAM]: '1' }
          : preservedExtras;
      const qs = serializeRecipeFilters(next, extras);
      const target = qs ? `${pathname}?${qs}` : pathname;
      startTransition(() => {
        router.push(target, { scroll: false });
      });
    },
    [router, pathname, preservedExtras],
  );

  // Mobile drawer: close on Escape, focus the close button, trap Tab within panel
  useEffect(() => {
    if (!open) return;
    closeBtnRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null);

      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', onKey);
    // Lock body scroll on mobile drawer
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Desktop popover: dismiss on outside click. The mobile bottom-sheet already
  // closes via its own tap-to-close scrim (`.backdrop`), so this is gated to
  // >=768px to avoid double-handling the same tap. We exclude the trigger so a
  // click on it isn't undone here (mousedown-close) then immediately re-opened
  // by its own onClick toggle (click-reopen).
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent) => {
      // Desktop only — mirrors the 768px `md` breakpoint used across the CSS.
      // Evaluated per-event so viewport resizes while open are handled.
      if (!window.matchMedia('(min-width: 768px)').matches) return;
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (toggleBtnRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  // Toggle helpers — each returns a new filter object
  const toggleDietary = (key: DietaryKey) => {
    const next = filters.dietary.includes(key)
      ? filters.dietary.filter((k) => k !== key)
      : [...filters.dietary, key];
    applyFilters({ ...filters, dietary: next });
  };

  const toggleCuisine = (id: string) => {
    const next = filters.cuisineIds.includes(id)
      ? filters.cuisineIds.filter((x) => x !== id)
      : [...filters.cuisineIds, id];
    applyFilters({ ...filters, cuisineIds: next });
  };

  const toggleCategory = (id: string) => {
    const next = filters.categoryIds.includes(id)
      ? filters.categoryIds.filter((x) => x !== id)
      : [...filters.categoryIds, id];
    applyFilters({ ...filters, categoryIds: next });
  };

  const setMaxCookTime = (m: number | null) => {
    applyFilters({ ...filters, maxCookTime: filters.maxCookTime === m ? null : m });
  };

  const setVariant = (v: VariantKey) => {
    applyFilters({ ...filters, variant: filters.variant === v ? null : v });
  };

  const toggleCooked = () => applyFilters({ ...filters, cooked: !filters.cooked });
  const toggleCookbook = () => applyFilters({ ...filters, cookbook: !filters.cookbook });

  // No special-casing needed: EMPTY_FILTERS has an empty dietary set, so
  // applyFilters stamps NO_DEFAULT_PARAM for us (#623).
  const clearAll = () => {
    applyFilters(EMPTY_FILTERS);
  };

  return (
    <div className={styles.wrap} data-pending={isPending ? 'true' : undefined}>
      <div className={styles.bar}>
        <button
          ref={toggleBtnRef}
          type="button"
          className={styles.toggleButton}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name="filter" size={16} aria-hidden="true" />
          <span>{t('recipes.filters.openFilters')}</span>
          {activeCount > 0 && (
            <span className={styles.badge} aria-label={t('recipes.filters.activeAria', { count: activeCount })}>
              {activeCount}
            </span>
          )}
        </button>

        {hasActiveFilters(filters) && (
          <Button type="button" variant="ghost" size="sm" onClick={clearAll}>
            {t('recipes.filters.clearAll')}
          </Button>
        )}
      </div>

      {open && (
        <>
          <div
            className={styles.backdrop}
            aria-hidden="true"
            onClick={() => setOpen(false)}
          />
          <div
            ref={panelRef}
            id={panelId}
            className={styles.panel}
            role="dialog"
            aria-modal="true"
            aria-labelledby="recipe-filters-drawer-title"
          >
            <div className={styles.drawerHeader}>
              <h2 id="recipe-filters-drawer-title" className={styles.drawerTitle}>{t('recipes.filters.title')}</h2>
              <Button
                ref={closeBtnRef}
                type="button"
                variant="ghost"
                size="sm"
                iconOnly
                leadingIcon="x"
                onClick={() => setOpen(false)}
                aria-label={t('recipes.filters.closeFilters')}
              />
            </div>

            {showLibrary && (
              <fieldset className={styles.group}>
                <legend className={styles.legend}>{t('recipes.filters.library')}</legend>
                <div className={styles.pills} role="group">
                  <OptionChip
                    leadingIcon="flame"
                    selected={filters.cooked}
                    onClick={toggleCooked}
                  >
                    {t('recipes.filters.cooked')}
                  </OptionChip>
                  <OptionChip
                    leadingIcon="book"
                    selected={filters.cookbook}
                    onClick={toggleCookbook}
                  >
                    {t('recipes.filters.inCookbook')}
                  </OptionChip>
                </div>
              </fieldset>
            )}

            <fieldset className={styles.group}>
              <legend className={styles.legend}>{t('recipes.filters.dietary')}</legend>
              <div className={styles.pills} role="group">
                {visibleDietaryKeys.map((k) => (
                  <OptionChip
                    key={k}
                    leadingIcon={DIET_FILTER_ICON[k]}
                    selected={filters.dietary.includes(k)}
                    onClick={() => toggleDietary(k)}
                  >
                    {dietaryLabels[k]}
                  </OptionChip>
                ))}
              </div>
            </fieldset>

            {categories.length > 0 && (
              <FilterPillGroup
                legend={t('recipes.filters.category')}
                type="checkbox"
                items={categories}
                selectedIds={filters.categoryIds}
                onToggle={toggleCategory}
                overflowAfter={6}
              />
            )}

            {cuisines.length > 0 && (
              <FilterPillGroup
                legend={t('recipes.filters.cuisine')}
                type="checkbox"
                items={cuisines}
                selectedIds={filters.cuisineIds}
                onToggle={toggleCuisine}
              />
            )}

            <FilterPillGroup
              legend={t('recipes.filters.variant')}
              type="radio"
              items={VARIANT_KEYS.map((v) => ({
                id: v,
                name: t(`recipes.variants.${v}`),
              }))}
              selectedIds={filters.variant !== null ? [filters.variant] : []}
              onToggle={(id) => setVariant(id as VariantKey)}
            />

            <FilterPillGroup
              legend={t('recipes.filters.maxCookTime')}
              type="radio"
              items={COOK_TIME_PRESETS.map((m) => {
                const key = COOK_TIME_KEY_BY_MIN[m];
                return {
                  id: String(m),
                  name: key
                    ? t(`recipes.filters.cookTime.${key}`)
                    : `≤ ${t('recipes.minutes', { count: m })}`,
                };
              })}
              selectedIds={filters.maxCookTime !== null ? [String(filters.maxCookTime)] : []}
              onToggle={(id) => setMaxCookTime(Number(id))}
            />

            <div className={styles.applyBar}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearAll}
                disabled={!hasActiveFilters(filters)}
              >
                {t('recipes.filters.clear')}
              </Button>
              <Button
                type="button"
                onClick={() => setOpen(false)}
              >
                {t('recipes.filters.showResults')}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pill group — wraps a <fieldset><legend> with checkbox- or radio-styled pills.
// ---------------------------------------------------------------------------

interface FilterPillGroupProps {
  legend: string;
  type: 'checkbox' | 'radio';
  items: TaxonomyItem[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  /**
   * When set, collapse the group to the first N chips behind a "+N more"
   * toggle. Any selected chip beyond the cap is always kept visible so an
   * active filter never hides. Used for the long category list.
   */
  overflowAfter?: number;
}

function FilterPillGroup({ legend, type, items, selectedIds, onToggle, overflowAfter }: FilterPillGroupProps) {
  const t = useTranslations();
  const [expanded, setExpanded] = useState(false);

  const collapsed = overflowAfter != null && !expanded && items.length > overflowAfter;
  const shown = collapsed
    ? // keep the first N plus any selected-but-hidden chip
      items.filter((item, i) => i < overflowAfter! || selectedIds.includes(item.id))
    : items;
  const hiddenCount = items.length - shown.length;

  return (
    <fieldset className={styles.group}>
      <legend className={styles.legend}>{legend}</legend>
      <div className={styles.pills} role={type === 'radio' ? 'radiogroup' : 'group'}>
        {shown.map((item) => (
          <OptionChip
            key={item.id}
            selected={selectedIds.includes(item.id)}
            onClick={() => onToggle(item.id)}
          >
            {item.name}
          </OptionChip>
        ))}
        {overflowAfter != null && items.length > overflowAfter && (
          <button
            type="button"
            className={styles.moreButton}
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded
              ? t('recipes.filters.showLess')
              : t('recipes.filters.showMore', { count: hiddenCount })}
          </button>
        )}
      </div>
    </fieldset>
  );
}
